import { Icon } from '../../../components/icons';
import { FeaturePageLayout, type FeatureContent } from './FeaturePageLayout';
import { BlacklistStory } from './stories/BlacklistStory';

const CONTENT: FeatureContent = {
  kind: 'blacklist',
  slug: 'blacklist-checks',
  story: <BlacklistStory />,
  title: 'Blacklist Checks',
  tagline: '50+ industry blacklists on every domain — before deliverability bites.',
  metaDescription:
    'Monitor IPs and sending domains against 90+ blacklists (RBLs, DNSBLs, SURBL, URIBL), with daily re-checks, drift alerts and history — catch listings early.',
  eyebrow: 'Credit-metered · Pro-bundled headroom',
  description:
    'Blacklist aggregation runs on top of every Domain Health snapshot. Hit count + per-listing detail surface alongside SPF / DKIM / DMARC posture so deliverability problems show up before they hit your production sending volume.',
  icon: <Icon.ShieldAlert width={48} height={48} />,
  accent: 'review',
  heroBg: '/heroes/feat-blacklist.webp',
  pillars: [
    {
      title: '50+ industry blacklists',
      body: 'One check covers the major sender, domain, and URI blacklists — dozens of lists in total. Each listing surfaces with its name + URL so you can open the delisting page in one click.',
    },
    {
      title: 'Per-domain hit count',
      body: 'Every checked domain gets a "0 hits / 50 checked" tile on its Domain Health page. Drill into the per-listing detail to see exactly which blacklists flagged you and where to go to dispute.',
    },
    {
      title: 'Built into Domain Health',
      body: 'No separate endpoint — results land on the matching /v1/domain-health/:domain snapshot. The Analytics page has a dedicated Blacklists tab for tenants who want to slice across the whole fleet.',
    },
  ],
  steps: [
    {
      title: 'Enable it in your workspace',
      body: 'Profile → Add-ons → Blacklist Checks. Every check draws from your monthly credit wallet at the weight shown in the Pricing section above.',
    },
    {
      title: 'We pre-flight',
      body: 'Blacklist checks piggy-back on every fresh Domain Health snapshot. The result is cached on the DH row alongside SPF / DKIM / DMARC posture — subsequent lookups of the same domain within the 24h TTL return the stored blacklist result for free. A fresh check only runs when the DH snapshot expires and gets refreshed.',
    },
    {
      title: 'Hit count surfaces',
      body: 'On the per-domain page + as a sub-pill on the per-record Domain Health badge. 0 hits = green; ≥1 = red.',
    },
    {
      title: 'Drill into the listings',
      body: 'Per-blacklist name + URL + flag status. Top-blacklist roll-up on the Blacklists Analytics tab shows which lists are most aggressive against your fleet.',
    },
  ],
  included: [
    {
      scope: 'trial',
      items: [
        'A few checks to validate the workflow',
        'Hit count + per-listing detail',
        'Blacklists Analytics tab roll-up',
      ],
    },
    {
      scope: 'pro',
      items: [
        'Pro 10k+ plans bundle a monthly credit allowance that covers typical fleet volumes',
        'API access via /v1/domain-health',
        'Piggy-backs on Domain Health — cached with each DH snapshot for 24h, so re-checks of the same domain within the TTL return the stored result for free',
      ],
    },
    {
      scope: 'addon',
      items: [
        'Top up with a credit pack when your monthly allowance runs low',
        'Same per-check credit weight whether credits come from the plan or a pack',
        'Cache TTL is 24h — a domain re-referenced today after being checked yesterday spends no credits',
      ],
    },
  ],
  faq: [
    {
      q: 'How are blacklist checks priced?',
      a: 'Each check consumes the credit weight shown in the Pricing section above, drawn from your monthly wallet. Pro plans bundle enough monthly credits to cover typical fleet volumes; if you audit a big batch and run out, a credit pack tops up the wallet without changing your subscription.',
    },
    {
      q: 'Why is it bundled with Domain Health?',
      a: "Blacklist checks query the same domain rows DH does and share its 24h TTL cache — the hit count is stamped straight onto the DH snapshot so SPF / DKIM / DMARC posture surfaces next to the listing state on every record. Splitting them would mean two console screens and two analytics tabs for what's really one question: 'is this domain in a state to send?'",
    },
    {
      q: 'How do I delist?',
      a: "We surface each listing's URL — click through to the blacklist's own delisting form. We don't auto-delist (most blacklists require human operator review and don't expose an API). What we DO give you is a domain.blacklisted webhook that fires the moment a clean domain transitions to listed, so your on-call system knows to act before the listing hits your sending reputation.",
    },
  ],
  addonKind: 'blacklist',
  relatedFeatures: [
    {
      slug: 'domain-health',
      title: 'Domain Health',
      tagline: 'SPF / DKIM / DMARC posture surfaces next to every blacklist check.',
    },
    {
      slug: 'inbox-placement',
      title: 'Inbox Placement',
      tagline: 'Diagnose where your mail is landing when a blacklist hit is hurting it.',
    },
    {
      slug: 'warmup',
      title: 'Warmup',
      tagline: 'Mailbox-level reputation building, alongside domain-level posture.',
    },
  ],
};

export function BlacklistFeaturePage() {
  return <FeaturePageLayout content={CONTENT} />;
}

// Default export required by React Router framework-mode route modules.
export default BlacklistFeaturePage;
